/**
 * Visitor journey — telemetría en tiempo real a PostHog con contexto completo.
 *
 * Responde: de dónde vino, device, geo, qué vio, hasta dónde llegó, si casi compró,
 * si convirtió (validado server-side vía Stripe/MP webhook → mirror en PostHog).
 *
 * Evento canónico: `visitor_journey_realtime`
 * Complementa Slack (/api/track-funnel-failure) con el mismo journey snapshot.
 */

import { detectTrafficSource } from "@/lib/detectTrafficSource";
import { isInstagramBrowser } from "@/lib/instagramBrowserDetection";
import {
  getPageEngagementContext,
  markSectionViewed,
} from "@/lib/posthogEngagementContext";
import {
  getPostHogSlackSessionPropertiesPostHog,
  getSlackFunnelDiagnosticsExtraPostHog,
  isPostHogConfigured,
} from "@/lib/posthogAnalytics";
import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { getWorkerGeoAnalyticsProps } from "@/lib/serverTemplateDetection";

const JOURNEY_KEY = "ph_visitor_journey_v1";
const EMAIL_KEY = "ph_recovery_email_v1";
const MAX_STEPS = 40;

export type JourneyIntentLevel =
  | "visit"
  | "engaged"
  | "high_intent"
  | "checkout_started"
  | "almost_bought"
  | "converted"
  | "abandoned";

export interface JourneyStep {
  step: string;
  at: number;
  path?: string;
}

export interface JourneySnapshot {
  steps: JourneyStep[];
  intent_level: JourneyIntentLevel;
  funnel_depth: number;
  captured_email_domain?: string;
  recovery_eligible: boolean;
  last_funnel?: string;
  last_surface?: string;
  session_started_at: number;
}

function readJourney(): JourneySnapshot {
  const now = Date.now();
  if (typeof sessionStorage === "undefined") {
    return {
      steps: [],
      intent_level: "visit",
      funnel_depth: 0,
      recovery_eligible: false,
      session_started_at: now,
    };
  }
  try {
    const raw = sessionStorage.getItem(JOURNEY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<JourneySnapshot>;
      return {
        steps: Array.isArray(parsed.steps) ? parsed.steps.slice(-MAX_STEPS) : [],
        intent_level: (parsed.intent_level as JourneyIntentLevel) || "visit",
        funnel_depth: typeof parsed.funnel_depth === "number" ? parsed.funnel_depth : 0,
        captured_email_domain: parsed.captured_email_domain,
        recovery_eligible: parsed.recovery_eligible === true,
        last_funnel: parsed.last_funnel,
        last_surface: parsed.last_surface,
        session_started_at:
          typeof parsed.session_started_at === "number" ? parsed.session_started_at : now,
      };
    }
  } catch {
    /* corrupt */
  }
  return {
    steps: [],
    intent_level: "visit",
    funnel_depth: 0,
    recovery_eligible: false,
    session_started_at: now,
  };
}

function writeJourney(j: JourneySnapshot): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(JOURNEY_KEY, JSON.stringify(j));
  } catch {
    /* quota */
  }
}

function emailDomain(email: string): string | undefined {
  const at = email.trim().toLowerCase().lastIndexOf("@");
  if (at < 1) return undefined;
  return email.slice(at + 1).slice(0, 120);
}

const INTENT_RANK: Record<JourneyIntentLevel, number> = {
  visit: 0,
  engaged: 1,
  high_intent: 2,
  checkout_started: 3,
  almost_bought: 4,
  abandoned: 3,
  converted: 5,
};

function maxIntent(
  current: JourneyIntentLevel,
  next: JourneyIntentLevel,
): JourneyIntentLevel {
  return INTENT_RANK[next] > INTENT_RANK[current] ? next : current;
}

/** Registra un paso en el journey de la sesión (idempotente por step+minuto). */
export function recordJourneyStep(
  step: string,
  opts?: {
    intentLevel?: JourneyIntentLevel;
    funnel?: string;
    surface?: string;
    sectionId?: string;
  },
): JourneySnapshot {
  const j = readJourney();
  const now = Date.now();
  const path =
    typeof window !== "undefined" ? window.location.pathname : undefined;

  if (opts?.sectionId) markSectionViewed(opts.sectionId);

  const last = j.steps[j.steps.length - 1];
  if (!last || last.step !== step || now - last.at > 30_000) {
    j.steps.push({ step, at: now, path });
    if (j.steps.length > MAX_STEPS) j.steps = j.steps.slice(-MAX_STEPS);
  }

  j.funnel_depth = j.steps.length;
  if (opts?.intentLevel) {
    j.intent_level = maxIntent(j.intent_level, opts.intentLevel);
  }
  if (opts?.funnel) j.last_funnel = opts.funnel;
  if (opts?.surface) j.last_surface = opts.surface;

  writeJourney(j);
  return j;
}

/** Guarda email para recovery (dominio en journey; identify en PostHog si hay SDK). */
export function setJourneyRecoveryEmail(email: string): JourneySnapshot {
  const domain = emailDomain(email);
  const j = readJourney();
  if (domain) j.captured_email_domain = domain;
  j.recovery_eligible = true;
  j.intent_level = maxIntent(j.intent_level, "checkout_started");
  writeJourney(j);

  if (typeof sessionStorage !== "undefined") {
    try {
      sessionStorage.setItem(EMAIL_KEY, email.trim().toLowerCase().slice(0, 200));
    } catch {
      /* */
    }
  }

  if (isPostHogConfigured() && typeof window !== "undefined") {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ph = (window as any).posthog;
      if (typeof ph?.identify === "function") {
        ph.identify(email.trim().toLowerCase(), {
          email_domain: domain,
          recovery_eligible: true,
        });
      }
      if (typeof ph?.setPersonProperties === "function") {
        ph.setPersonProperties({
          recovery_eligible: true,
          last_checkout_email_domain: domain,
        });
      }
    } catch {
      /* */
    }
  }

  return j;
}

export function getJourneySnapshot(): JourneySnapshot {
  return readJourney();
}

/** Props serializables para /api/track-funnel-failure (string | number | boolean). */
export function getPostHogIdsForServer(): Record<string, string> {
  return getPostHogSlackSessionPropertiesPostHog();
}

export function getJourneyPayloadForServer(): Record<string, string | number | boolean> {
  const j = readJourney();
  const out: Record<string, string | number | boolean> = {
    funnel_depth: j.funnel_depth,
    journey_intent_level: j.intent_level,
    recovery_eligible: j.recovery_eligible,
  };
  if (j.captured_email_domain) out.captured_email_domain = j.captured_email_domain;
  if (j.last_funnel) out.journey_funnel = j.last_funnel;
  if (j.last_surface) out.journey_surface = j.last_surface;
  const steps = j.steps.map((s) => s.step).slice(-12);
  if (steps.length) out.journey_steps = steps.join("→").slice(0, 320);
  const sec = Math.max(0, Math.round((Date.now() - j.session_started_at) / 1000));
  out.journey_session_seconds = sec;
  return out;
}

function intentFromEventKind(kind: string): JourneyIntentLevel | undefined {
  if (kind.startsWith("cta_click_") || kind === "hero_cta_impression") {
    return "high_intent";
  }
  if (
    kind === "cumple_checkout_started" ||
    kind === "checkout_error" ||
    kind === "stripe_session_failed" ||
    kind === "stripe_checkout_initiated" ||
    kind === "dual_cta_checkout_initiated" ||
    kind === "almost_bought_mp_redirect"
  ) {
    return "almost_bought";
  }
  if (
    kind === "dual_cta_vip_impression" ||
    kind === "cta_click_vip" ||
    kind === "cta_click_free_telegram"
  ) {
    return "high_intent";
  }
  if (
    kind === "high_intent_abandonment" ||
    kind === "engagement_dropoff" ||
    kind === "cumple_abandonment" ||
    kind === "checkout_abandoned" ||
    kind === "mux_playback_error"
  ) {
    return "abandoned";
  }
  if (kind === "sale_confirmed" || kind === "payment_recurring") {
    return "converted";
  }
  if (kind.startsWith("page_enter_")) return "visit";
  return undefined;
}

/**
 * Captura en PostHog con contexto máximo (device, source, geo, scroll, journey).
 * Best-effort — nunca rompe UX.
 */
export function captureVisitorJourneyRealtime(opts: {
  eventKind: string;
  template?: string;
  country?: string;
  funnel?: string;
  productSurface?: string;
  lastStep?: string;
  label?: string;
  href?: string;
  recordStep?: string;
  intentLevel?: JourneyIntentLevel;
  extra?: Record<string, string | number | boolean | undefined>;
  replayNearNow?: boolean;
}): void {
  if (typeof window === "undefined" || !isPostHogConfigured()) return;

  const inferredIntent =
    opts.intentLevel ?? intentFromEventKind(opts.eventKind) ?? undefined;

  let journey = readJourney();
  if (opts.recordStep) {
    journey = recordJourneyStep(opts.recordStep, {
      intentLevel: inferredIntent,
      funnel: opts.funnel,
      surface: opts.productSurface,
    });
  } else if (inferredIntent) {
    journey.intent_level = maxIntent(journey.intent_level, inferredIntent);
    writeJourney(journey);
  }

  const ts = detectTrafficSource();
  const geo = getWorkerGeoAnalyticsProps();
  const engagement = getPageEngagementContext();
  const slackDiag = getSlackFunnelDiagnosticsExtraPostHog(
    opts.replayNearNow ? { replayNearNow: true } : {},
  );
  const phIds = getPostHogSlackSessionPropertiesPostHog(
    opts.replayNearNow ? { replayNearNow: true } : undefined,
  );

  const payload: Record<string, unknown> = {
    funnel_event_kind: opts.eventKind,
    realtime: true,
    template: opts.template,
    country: opts.country,
    funnel: opts.funnel ?? journey.last_funnel,
    product_surface: opts.productSurface ?? journey.last_surface,
    last_step: opts.lastStep ?? opts.recordStep,
    label: opts.label,
    href: opts.href?.slice(0, 500),
    channel: ts.channel,
    source: ts.source,
    brand_source: BRAND_SOURCE_KEY, // for brand isolation, mirrors main dual/vip events
    medium: ts.medium,
    in_app_browser: ts.inAppBrowser || isInstagramBrowser(),
    embedded_social_browser: isInstagramBrowser(),
    journey_intent_level: journey.intent_level,
    funnel_depth: journey.funnel_depth,
    journey_steps: journey.steps.map((s) => s.step).slice(-12),
    recovery_eligible: journey.recovery_eligible,
    captured_email_domain: journey.captured_email_domain,
    journey_session_seconds: Math.max(
      0,
      Math.round((Date.now() - journey.session_started_at) / 1000),
    ),
    sections_viewed: engagement.sections_viewed,
    max_scroll_pct: engagement.max_scroll_pct,
    time_on_page_seconds: engagement.time_on_page_seconds,
    ...geo,
    ...slackDiag,
    ...phIds,
    ...opts.extra,
  };

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ph = (window as any).posthog;
    if (typeof ph?.capture !== "function") return;
    ph.capture("visitor_journey_realtime", payload);
  } catch {
    /* */
  }
}